/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Grid } from '@mui/material';
import PageMainTitle from 'components/cmn/PageMainTitle';
import Divider from '@mui/material/Divider';
import useProjectInfoProvider from 'hooks/useProjectInfoProvider';
import { useAppDispatch } from 'store';
import Stepper1 from 'components/project/enter-information/Stepper1';
import { changeStepper1Value } from 'store/project/projectSlice';
import { IContent } from 'interface/project/projectInfo';
import dayjs from 'dayjs';
import { informationDCSteps, informationSteps } from 'libs/mindUtils';
import Stepper2 from './Stepper2';
import HeaderStep from './HeaderStep';
import FooterButton from './FooterButton';
import Stepper4 from './Stepper4';
import Stepper5 from './Stepper5';
import Stepper3 from './Stepper3';
import StepperDC1 from './dc/StepperDC1';
import FooterButtonDC from './dc/FooterButtonDC';
import StepperDC2 from './dc/StepperDC2';
import StepperDC3 from './dc/StepperDC3';
import StepperDC4 from './dc/StepperDC4';
import StepperDC5 from './dc/StepperDC5';

const stepComponent: { [key: number]: JSX.Element } = {
  0: <Stepper1 />,
  1: <Stepper2 />,
  2: <Stepper3 />,
  3: <Stepper4 />,
  4: <Stepper5 />,
};

const stepComponentByDC: { [key: number]: JSX.Element } = {
  0: <StepperDC1 />,
  1: <StepperDC2 />,
  2: <StepperDC3 />,
  3: <StepperDC4 />,
  4: <StepperDC5 />,
};

function InformationStepper({ projectType, detailInfo }: { projectType: string; detailInfo: IContent }): JSX.Element {
  const dispatch = useAppDispatch();
  const { inputs, onChange, dispatch: dispatchProjectInfo, onChangeDC, dcInputs, onReset } = useProjectInfoProvider();
  const { activeStep, completed } = inputs;
  const { activeStep: activeStepByDC } = dcInputs;

  useEffect(() => {
    if (projectType === 'ID') {
      onChange('projectType', projectType);
    } else if (projectType === 'DC') {
      onChangeDC('projectType', projectType);
    }
  }, [onChange, onChangeDC, projectType]);

  const isEmptyObject = (obj: object): boolean => {
    return Object.keys(obj).length === 0;
  };

  useEffect(() => {
    if (!isEmptyObject(detailInfo)) {
      dispatchProjectInfo({
        type: projectType === 'ID' ? 'handleDetailData' : 'handleDetailDataByDC',
        payload: {
          projectId: detailInfo.id,
          projectType: detailInfo.projectType,
          projectName: detailInfo.projectName,
          projectStartAt: dayjs(detailInfo.projectStartAt, 'YYYYMMDD'),
          projectEndAt: dayjs(detailInfo.projectEndAt, 'YYYYMMDD'),
          projectPartUserFileName: detailInfo?.projectPartUserFileName || '',
          compName: detailInfo.compName,
          compAdminList: detailInfo.projectAdmins,
        },
      });
      dispatch(
        changeStepper1Value({
          projectAdmins: detailInfo.projectAdmins,
          tmpProjectAdmins: [],
        }),
      );
    } else {
      onReset();
    }
  }, [
    detailInfo,
    detailInfo.compName,
    detailInfo.id,
    detailInfo.projectAdmins,
    detailInfo.projectEndAt,
    detailInfo.projectName,
    detailInfo.projectStartAt,
    detailInfo.projectType,
    dispatch,
    dispatchProjectInfo,
    projectType,
    onReset,
  ]);

  useEffect(() => {
    return () => {
      dispatch(
        changeStepper1Value({
          projectAdmins: [],
          tmpProjectAdmins: [],
        }),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalSteps = () => informationSteps.length;
  const completedSteps = () => Object.keys(completed).length;
  const allStepsCompleted = () => completedSteps() === totalSteps();

  const handleReset = () => {
    onChange('activeStep', 0);
    onChange('completed', {});
  };

  const checkInformationSteps = projectType === 'ID' ? informationSteps : informationDCSteps;

  return (
    <Box sx={{ width: '100%' }}>
      <HeaderStep projectType={projectType} />
      <div>
        {allStepsCompleted() ? (
          <>
            <Typography sx={{ mt: 2, mb: 1 }}>All steps completed - you&apos;re finished</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
              <Box sx={{ flex: '1 1 auto' }} />
              <Button onClick={handleReset}>Reset</Button>
            </Box>
          </>
        ) : (
          <Grid item xs={12} sx={{ mt: 2, mb: 1, py: 1 }}>
            <PageMainTitle
              title={projectType === 'ID' ? checkInformationSteps[activeStep] : checkInformationSteps[activeStepByDC]}
              fontSize={26}
            />
            <Divider sx={{ borderColor: 'mind.black.A0', width: '100%', my: 1 }} />
            {projectType === 'ID' ? stepComponent[activeStep] : stepComponentByDC[activeStepByDC]}
            {projectType === 'ID' ? <FooterButton /> : <FooterButtonDC />}
          </Grid>
        )}
      </div>
    </Box>
  );
}

export default InformationStepper;
